// Real BCM photography only.
//
// TWO SOURCES
// • "2026–27 BCM Photos" — the current-year album (Summer ’26, Fall ’26, Spring ’27).
// • "Public BCM Photos" — verified historical photography from Fall 2025 and
//   Spring 2026, used as a temporary visual source until new Fall 2026
//   photography exists.
//
// HOW TO MAINTAIN THIS FILE
// Paste each photo's hosted URL into `src`. Until then the frame renders a
// neutral "Add Image here" placeholder — never stock or AI-generated people.
// Historical photos must never be presented as upcoming events, current
// leadership, or a current schedule. Only add a `caption` when the source
// folder clearly identifies the event.

export type AlbumName =
  | 'Summer ’26'
  | 'Fall ’26'
  | 'Spring ’27'
  | 'Fall ’25'
  | 'Spring ’26'

export type BcmPhoto = {
  id: string
  album: AlbumName
  /** Verified subject/event from the source folder. */
  subject: string
  /** Meaningful, general alt text for the real image. */
  alt: string
  /** Only set when the source folder clearly identifies the event. */
  caption?: string
  /** Hosted URL of the real BCM photograph. Leave empty until supplied. */
  src?: string
}

export const albumMeta: {
  name: AlbumName
  hasPhotos: boolean
  status: string
  /** Public-facing message shown when the folder has no photos yet. */
  message?: string
  note: string
}[] = [
  {
    name: 'Summer ’26',
    hasPhotos: true,
    status: 'Photos available',
    note: 'Pickleball Night and Tabling photography.',
  },
  {
    name: 'Fall ’26',
    hasPhotos: false,
    status: 'Coming soon',
    message: 'Fall photos will be added after the first worship night on August 27.',
    note: 'Folder created — no photos yet.',
  },
  {
    name: 'Spring ’27',
    hasPhotos: false,
    status: 'Coming soon',
    message: 'Spring photos will be added as the semester begins.',
    note: 'Folder created — no photos yet.',
  },
]

/** Current-year album: 2026–27 BCM Photos. */
export const currentYearPhotos: BcmPhoto[] = [
  {
    id: 'summer26-pickleball-1',
    album: 'Summer ’26',
    subject: 'Pickleball Night',
    alt: 'UCF BCM students playing together at a pickleball night.',
    caption: 'Pickleball Night · Summer ’26',
  },
  {
    id: 'summer26-tabling-1',
    album: 'Summer ’26',
    subject: 'Tabling',
    alt: 'UCF BCM students tabling on campus and meeting new students.',
    caption: 'Tabling on campus · Summer ’26',
  },
  {
    id: 'summer26-pickleball-2',
    album: 'Summer ’26',
    subject: 'Pickleball Night',
    alt: 'UCF BCM students talking and laughing between games.',
    caption: 'Pickleball Night · Summer ’26',
  },
  {
    id: 'summer26-tabling-2',
    album: 'Summer ’26',
    subject: 'Tabling',
    alt: 'A UCF BCM student sharing information about Thursday worship nights.',
    caption: 'Tabling on campus · Summer ’26',
  },
]

/**
 * Historical album: Public BCM Photos (Fall 2025 + Spring 2026).
 * Temporary visual source — never labeled as an upcoming event or as the
 * current leadership team.
 */
export const pastPhotos: BcmPhoto[] = [
  {
    id: 'past-worship-1',
    album: 'Fall ’25',
    subject: 'Worship night',
    alt: 'UCF BCM students gathering at a worship night.',
    caption: 'Worship night · Fall ’25',
  },
  {
    id: 'past-community-group-1',
    album: 'Fall ’25',
    subject: 'Community group',
    alt: 'UCF BCM students sitting together during a community group.',
    caption: 'Community group · Fall ’25',
  },
  {
    id: 'past-volleyball-1',
    album: 'Fall ’25',
    subject: 'Volleyball night',
    alt: 'UCF BCM students playing volleyball together.',
    caption: 'Volleyball night · Fall ’25',
  },
  {
    id: 'past-outreach-1',
    album: 'Fall ’25',
    subject: 'Campus outreach',
    alt: 'UCF BCM students meeting other students during campus outreach.',
    caption: 'Campus outreach · Fall ’25',
  },
  {
    id: 'past-friendsgiving-1',
    album: 'Fall ’25',
    subject: 'Friendsgiving',
    alt: 'UCF BCM students sharing a meal together at Friendsgiving.',
    caption: 'Friendsgiving · Fall ’25',
  },
  {
    id: 'past-camping-1',
    album: 'Fall ’25',
    subject: 'Camping trip',
    alt: 'UCF BCM students on a camping trip together.',
    caption: 'Camping trip · Fall ’25',
  },
  {
    id: 'past-formal-1',
    album: 'Spring ’26',
    subject: 'Formal',
    alt: 'UCF BCM students dressed up together at a formal event.',
    caption: 'Formal · Spring ’26',
  },
  {
    id: 'past-collegiate-weekend-1',
    album: 'Spring ’26',
    subject: 'Collegiate Weekend',
    alt: 'UCF BCM students together during Collegiate Weekend.',
    caption: 'Collegiate Weekend · Spring ’26',
  },
  {
    id: 'past-worship-2',
    album: 'Spring ’26',
    subject: 'Worship night',
    alt: 'UCF BCM students singing together during a worship night.',
    caption: 'Worship night · Spring ’26',
  },
  {
    id: 'past-community-group-2',
    album: 'Spring ’26',
    subject: 'Community group',
    alt: 'UCF BCM students talking and praying together in a community group.',
    caption: 'Community group · Spring ’26',
  },
]

const byId = (id: string): BcmPhoto => {
  const all = [...currentYearPhotos, ...pastPhotos]
  const found = all.find((photo) => photo.id === id)
  if (!found) throw new Error(`Unknown photo id: ${id}`)
  return found
}

/** Hero collage: friendship, recreation, and outreach — a deliberate mix. */
export const heroPhotos: BcmPhoto[] = [
  byId('past-worship-1'),
  byId('summer26-pickleball-1'),
  byId('summer26-tabling-1'),
]

/** "Come as you are" pair: community group + shared meal. */
export const galleryPhotos: BcmPhoto[] = [
  byId('past-community-group-1'),
  byId('past-friendsgiving-1'),
  byId('past-outreach-1'),
]

/** Homepage mosaic: worship, recreation, outreach, trips, friendship. */
export const mosaicPhotos: BcmPhoto[] = [
  byId('past-worship-2'),
  byId('past-volleyball-1'),
  byId('past-camping-1'),
  byId('past-collegiate-weekend-1'),
  byId('summer26-pickleball-2'),
  byId('past-formal-1'),
]

/** Typical Week section photo. */
export const typicalWeekPhoto: BcmPhoto = byId('past-community-group-2')

/** First-visit section photo. */
export const firstVisitPhoto: BcmPhoto = byId('past-worship-1')

/** Temporary poster image for the welcome video component. */
export const videoPosterPhoto: BcmPhoto = byId('past-worship-2')
